import { useState } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader, Panel, StatTile, Sparkline, EmptyState, Button, FormRow, Textarea, useToast } from '../ds/index'
import { post } from '../lib/api'
import { HBars } from '../components/Viz'
import { useApi } from '../lib/useApi'
import { ago, money, num } from '../lib/format'
import { LoadingBlock, ErrorBlock } from '../components/Loading'
import { LeadFilterBar, useLeadFilters, useFilterOptions } from '../components/LeadFilters'

type Cat = {
  id: string | null; name: string; active: boolean
  leads: number; sold_leads: number; sales: number; revenue_cents: number; waiting: number; no_buyer: number; contacted: number
  last_lead_at: string | null; buyers: number; reps: number
  top_areas: { name: string; n: number }[]; daily_14: number[]
}

type Demand = { name: string; n: number; last_at: string; groups: number; examples: { id: string; text: string; first_seen_at: string }[] }

const DAYS_LABEL: Record<string, string> = { '1': 'today', '7': 'in the last 7 days', '30': 'in the last 30 days', '90': 'in the last 90 days', all: 'all time' }

export default function Categories() {
  const f = useLeadFilters('30')
  const options = useFilterOptions()
  const { data, error, reload } = useApi<{ categories: Cat[]; uncategorized: Demand[] }>(`/api/categories/overview?${f.query}`)
  const cats = data?.categories || []
  const total = cats.reduce((t, c) => ({ leads: t.leads + c.leads, sold: t.sold + c.sold_leads, revenue: t.revenue + c.revenue_cents, waiting: t.waiting + c.waiting, none: t.none + c.no_buyer }), { leads: 0, sold: 0, revenue: 0, waiting: 0, none: 0 })
  // A link into the board for one category and one slice of it, keeping every other filter.
  const board = (c: Cat, view = 'leads') => `/leads${f.carry({ category: c.id ?? 'none', view })}`

  return (
    <>
      <PageHeader title="Leads by category" subtitle={`What people are asking for ${DAYS_LABEL[f.days]}, how much of it sold, and where it is stuck.`}
                  actions={<Link className="btn btn-ghost" to={`/leads${f.carry()}`}>Lead board</Link>} />
      <LeadFilterBar f={f} options={options} only={['days', 'area', 'group', 'rep', 'buyer', 'q']} />
      {error && <ErrorBlock error={error} />}
      {!data ? <LoadingBlock /> : (
        <>
          <div className="grid-tiles" style={{ marginBottom: 'var(--s-6)' }}>
            <StatTile focal label="Leads" value={num(total.leads)} meta={`across ${cats.filter((c) => c.leads).length} categories`} />
            <StatTile label="Sold" value={num(total.sold)} meta={total.leads ? `${Math.round((total.sold / total.leads) * 100)}% of leads` : 'none yet'} progress={total.leads ? (total.sold / total.leads) * 100 : undefined} />
            <StatTile label="Sold for" value={money(total.revenue, { compact: true })} meta="billed to buyers" />
            <StatTile label="Stuck" value={num(total.waiting + total.none)} meta={`${total.waiting} waiting on a rep, ${total.none} with no buyer`} />
          </div>

          {cats.length === 0 ? <Panel><EmptyState title="No leads in this window" body="Try a longer date range or fewer filters." /></Panel> : (
            <>
              <Panel>
                <HBars title="Leads per category" subtitle="Bars are leads; the note is how many sold" format={(n) => num(n)}
                       rows={cats.filter((c) => c.leads).map((c) => ({ label: c.name, value: c.leads, note: `${c.sold_leads} sold` }))} />
              </Panel>
              <div style={{ height: 'var(--s-6)' }} />
              <div className="cat-grid">
                {cats.map((c) => (
                  <article key={c.id ?? 'none'} className={`panel cat-card${c.id ? '' : ' cat-none'}`}>
                    <header className="cat-head">
                      <Link to={board(c)} className="cat-name">{c.name}{!c.active && <span className="muted"> (inactive)</span>}</Link>
                      <span className="muted">{c.last_lead_at ? `last lead ${ago(c.last_lead_at)}` : 'no leads'}</span>
                    </header>
                    <div className="cat-figures">
                      <Link to={board(c)}><span className="num cat-big">{num(c.leads)}</span><span className="muted">leads</span></Link>
                      <Link to={board(c, 'sold')}><span className="num cat-big">{num(c.sold_leads)}</span><span className="muted">sold{c.revenue_cents ? `, ${money(c.revenue_cents, { compact: true })}` : ''}</span></Link>
                      <Link to={board(c, 'open')} className={c.waiting ? 'tone-warn' : undefined}><span className="num cat-big">{num(c.waiting)}</span><span className="muted">waiting on a rep</span></Link>
                      <Link to={board(c, 'unrouted')} className={c.no_buyer ? 'tone-warn' : undefined}><span className="num cat-big">{num(c.no_buyer)}</span><span className="muted">no buyer</span></Link>
                    </div>
                    <Sparkline data={c.daily_14.map((n, i) => ({ i, n }))} valueKey="n" height={30} />
                    <p className="muted cat-spark-label">Leads per day, last 14 days</p>
                    <dl className="cat-facts">
                      <div><dt>Top areas</dt><dd>{c.top_areas.length ? c.top_areas.map((a) => `${a.name} (${a.n})`).join(', ') : 'none yet'}</dd></div>
                      <div><dt>Buyers</dt><dd>{c.id ? (c.buyers ? `${c.buyers} active, carried by ${c.reps} rep${c.reps === 1 ? '' : 's'}` : <span className="tone-warn">No active buyer: these leads cannot be sold</span>) : 'Screening could not place these. Check the categories.'}</dd></div>
                      <div><dt>Buyer said they heard</dt><dd>{c.sales ? `${c.contacted} of ${c.sales} sale${c.sales === 1 ? '' : 's'}` : 'no sales yet'}</dd></div>
                    </dl>
                  </article>
                ))}
              </div>
            </>
          )}
          <div style={{ height: 'var(--s-6)' }} />
          <Uncovered rows={data.uncategorized || []} days={DAYS_LABEL[f.days]} onDone={reload} />
        </>
      )}
    </>
  )
}

/**
 * Demand no category covers yet: real requests screening could not place,
 * grouped by the service they asked for. Making one a category screens its
 * recent requests again, and they become leads.
 */
function Uncovered({ rows, days, onDone }: { rows: Demand[]; days: string; onDone: () => void }) {
  const [open, setOpen] = useState<string | null>(null)
  return (
    <Panel>
      <div className="panel-header">
        <h2 className="panel-title">Requests we don't cover yet</h2>
        <span className="muted">{rows.reduce((t, r) => t + r.n, 0)} {days}</span>
      </div>
      <p className="muted" style={{ marginTop: 0 }}>Real requests for a service none of the categories covers. They go to no rep and nobody pays for them. When one keeps coming up, make it a category.</p>
      {rows.length === 0 ? <EmptyState title="Nothing uncovered" body="Every request in this window fit a category." /> : (
        <div className="stack-tight">
          {rows.map((r) => (
            <div key={r.name} className="uncovered-row">
              <div className="row-tight" style={{ justifyContent: 'space-between', flexWrap: 'wrap', alignItems: 'center' }}>
                <span><strong>{r.name}</strong> <span className="muted">{num(r.n)} request{r.n === 1 ? '' : 's'}, {r.groups} group{r.groups === 1 ? '' : 's'}, last {ago(r.last_at)}</span></span>
                {open !== r.name && <Button variant="secondary" onClick={() => setOpen(r.name)}>Make it a category</Button>}
              </div>
              <ul className="uncovered-examples">
                {r.examples.map((e) => <li key={e.id}><Link to={`/leads/${e.id}?view=uncategorized`}>{e.text || 'Text removed under the retention policy'}</Link></li>)}
              </ul>
              {open === r.name && <Promote demand={r} onCancel={() => setOpen(null)} onDone={() => { setOpen(null); onDone() }} />}
            </div>
          ))}
        </div>
      )}
    </Panel>
  )
}

function Promote({ demand, onCancel, onDone }: { demand: Demand; onCancel: () => void; onDone: () => void }) {
  const toast = useToast()
  const [name, setName] = useState(demand.name)
  const [description, setDescription] = useState('')
  const [busy, setBusy] = useState(false)
  const save = async () => {
    setBusy(true)
    try {
      const r = await post<{ rescreened: number; left: number }>('/api/categories/from-uncategorized', { suggested: demand.name, name, description })
      toast(`${name} is a category. ${r.rescreened ? `${r.rescreened} recent request${r.rescreened === 1 ? ' is' : 's are'} being screened again.` : ''}${r.left ? ` ${r.left} older one${r.left === 1 ? ' stays' : 's stay'} here.` : ''}`, 'pos')
      onDone()
    } catch (e) { toast((e as Error).message, 'neg'); setBusy(false) }
  }
  return (
    <div className="stack-tight" style={{ marginTop: 'var(--s-3)' }}>
      <FormRow label="Category name"><input className="field" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} /></FormRow>
      <FormRow label="What counts" help="Screening reads this. List the words people use: for pet care, vet, spay, neuter, grooming, pet sitter.">
        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
      </FormRow>
      <p className="muted" style={{ margin: 0 }}>Requests from the last 3 days are screened again and become leads for the reps in their groups. Older ones stay here, so no one is sent a stale request. Add clients in this category to sell them.</p>
      <div className="row-tight">
        <Button onClick={save} disabled={busy || name.trim().length < 2}>{busy ? 'Creating' : 'Create category'}</Button>
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  )
}
